import { Modal } from 'bootstrap'
import { Role } from 'auth0'
import { of, Subject } from 'rxjs'
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators'

import { Component, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { AuthenticationService, isAdminRole, isReadOnly, OrganizationMember } from '@services/authentication.service'
import { SubscriptionService } from '@services/subscription.service'
import { User } from '@classes/User'
import { UserService } from '@services/user.service'

@Component({
  selector: 'shared-modal-edit-member',
  templateUrl: './edit-member.component.html',
  styleUrls: ['./edit-member.component.css'],
  standalone: false,
})
export class EditMemberComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()
  public modal: Modal
  public member: OrganizationMember
  public dbUser: User
  public title = 'Edit Member'

  public organizationRoles: Role[] = []
  public adminSeatsAvailable: number = 0
  public readOnlySeatsAvailable: number = 0

  public form: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    employeeNumber: new UntypedFormControl(''),
    role: new UntypedFormControl(''),
  })

  get isReadOnly() { return isReadOnly }
  get isAdminRole() { return isAdminRole }

  get currentRoleId(): string {
    return this.member?.roles?.[0]?.id ?? ''
  }

  constructor(
    private _authService: AuthenticationService,
    private _subscriptionService: SubscriptionService,
    private _userService: UserService,
  ) { }

  ngOnInit() {
    this.initialize()
  }

  initialize() {
    if (!this.member) return

    this.form.patchValue({
      name: this.member.name ?? '',
      employeeNumber: this.dbUser?.employeeNumber ?? '',
      role: this.currentRoleId,
    })
  }

  seatsAvailable(roleId: string): number {
    if (isAdminRole(roleId)) {
      return this.adminSeatsAvailable ?? 0
    } else if (isReadOnly(roleId)) {
      return this.readOnlySeatsAvailable ?? 0
    }
    return 0
  }

  save() {
    const name = this.form.value.name?.trim() || this.member?.name
    const employeeNumber = this.form.value.employeeNumber?.trim() || null
    const selectedRoleId = this.form.value.role

    const roleChanged = selectedRoleId && selectedRoleId !== this.currentRoleId
    const selectedRole = this.organizationRoles.find(r => r.id === selectedRoleId)

    // Update name + employee number
    this._userService.updateMemberDetails(this.member.user_id, { name, employeeNumber }).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        if (this.member) this.member.name = name
        if (this.dbUser) {
          this.dbUser.employeeNumber = employeeNumber
          this.dbUser.name = name
        }
      }),
      // Refresh org users (backend may have created a new local DB record)
      switchMap(() => this._userService.getOrganizationsUsers()),
      tap(users => this._userService.organizationUsers = users),
      // Update role if changed
      switchMap(() => {
        if (roleChanged) {
          return this._authService.setMemberRole(this.member.user_id, selectedRoleId).pipe(
            tap(() => {
              if (selectedRole) {
                this.member.roles = [selectedRole]
              }
            }),
            switchMap(() => this._subscriptionService.getSubscriptionUsage()),
          )
        }
        return of(null)
      }),
    ).subscribe({
      next: () => this.modal?.hide(),
      error: () => {} // Error toast is shown by RequestService (toast: true)
    })
  }

  cancel() {
    this.modal?.hide()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
